import { useEffect, useState } from 'react'
import { X, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'

type EmailReceiptModalProps = {
  isOpen: boolean
  title: string
  defaultEmail?: string
  onClose: () => void
  onSend: () => Promise<void> | void
}

export function EmailReceiptModal({
  isOpen,
  title,
  defaultEmail,
  onClose,
  onSend,
}: EmailReceiptModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setError(null)
    }
  }, [isOpen, defaultEmail])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.classList.add('overflow-hidden')
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!defaultEmail) {
      setError('User does not have a valid email address')
      return
    }

    setError(null)
    setIsSubmitting(true)
    try {
      await onSend()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send receipt')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-120 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-md bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h2>
          </div>
          <button
            className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Recipient Email
            </label>
            <div className="px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm">
              {defaultEmail || 'No email on file'}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} isLoading={isSubmitting} disabled={!defaultEmail}>
              Send Receipt
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
